'use client';

import { toast } from 'sonner';

// Types
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  image?: string;
  email?: string;
  linkedin?: string;
  github?: string;
  twitter?: string;
  order: number;
  visible: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Statistic {
  id: string;
  label: string;
  value: string;
  description?: string;
  icon?: string;
  order: number;
  visible: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SiteSettings {
  id: string;
  companyName: string;
  tagline: string;
  description: string;
  email?: string;
  phone?: string;
  address?: string;
  linkedin?: string;
  github?: string;
  twitter?: string;
  instagram?: string;
  facebook?: string;
  primaryColor?: string;
  secondaryColor?: string;
  favicon?: string;
  logo?: string;
}

// Storage Keys
const STORAGE_KEYS = {
  TEAM: 'arithmify_team_members',
  STATS: 'arithmify_statistics',
  SETTINGS: 'arithmify_site_settings',
};

// Seed Data
const TEAM_SEED_DATA: TeamMember[] = [
  {
    id: 'team_1',
    name: 'Sofia Reyes',
    role: 'Product Designer',
    bio: 'Creative designer with 8 years of experience in digital products',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400',
    email: 'sofia@arithmify.io',
    linkedin: 'sofiareyesdesign',
    github: '',
    twitter: '@sofireyes',
    order: 0,
    visible: true,
  },
  {
    id: 'team_2',
    name: 'Marcus Chen',
    role: 'Lead Developer',
    bio: 'Full-stack engineer specializing in scalable web applications',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    email: 'marcus@arithmify.io',
    linkedin: 'marcuschen',
    github: 'marcuschen',
    twitter: '@marcuschen',
    order: 1,
    visible: true,
  },
  {
    id: 'team_3',
    name: 'Priya Nair',
    role: 'Project Manager',
    bio: 'Agile enthusiast focused on delivering excellence',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    email: 'priya@arithmify.io',
    linkedin: 'priyanair',
    github: '',
    twitter: '@priyanair',
    order: 2,
    visible: true,
  },
];

const STATS_SEED_DATA: Statistic[] = [
  {
    id: 'stat_1',
    label: 'Projects Completed',
    value: '180+',
    description: 'Successful projects delivered across industries',
    icon: '📊',
    order: 0,
    visible: true,
  },
  {
    id: 'stat_2',
    label: 'Team Members',
    value: '45',
    description: 'Skilled professionals ready to help',
    icon: '👥',
    order: 1,
    visible: true,
  },
  {
    id: 'stat_3',
    label: 'Client Satisfaction',
    value: '98%',
    description: 'Average client satisfaction rate',
    icon: '⭐',
    order: 2,
    visible: true,
  },
  {
    id: 'stat_4',
    label: 'Years in Business',
    value: '8+',
    description: 'Trusted by companies globally',
    icon: '🏆',
    order: 3,
    visible: true,
  },
];

// Fallback to localStorage when API fails
const isLocalStorage = typeof window !== 'undefined';

// Helper: Try API first, fallback to localStorage
async function tryAPI<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: any
): Promise<T | null> {
  try {
    const options: RequestInit = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (data) options.body = JSON.stringify(data);

    const response = await fetch(endpoint, options);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return await response.json();
  } catch (error) {
    // API failed - will use localStorage
    return null;
  }
}

// ===== TEAM MEMBERS =====
export const teamStorage = {
  async getAll(): Promise<TeamMember[]> {
    // Try API first
    const apiData = await tryAPI<TeamMember[]>('/api/team');
    if (apiData) return apiData;

    // Fallback to localStorage
    if (!isLocalStorage) return TEAM_SEED_DATA;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TEAM);
      if (stored) return JSON.parse(stored);
      // Return seed data as default
      return TEAM_SEED_DATA;
    } catch {
      return TEAM_SEED_DATA;
    }
  },

  async create(data: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<TeamMember> {
    const id = `team_${Date.now()}`;
    const member: TeamMember = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Try API first
    const apiResult = await tryAPI<TeamMember>('/api/team', 'POST', member);
    if (apiResult) return apiResult;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      all.push(member);
      localStorage.setItem(STORAGE_KEYS.TEAM, JSON.stringify(all));
    }
    return member;
  },

  async update(id: string, data: Partial<TeamMember>): Promise<TeamMember | null> {
    // Try API first
    const apiResult = await tryAPI<TeamMember>('/api/team', 'PUT', { id, ...data });
    if (apiResult) return apiResult;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      const index = all.findIndex((m) => m.id === id);
      if (index !== -1) {
        all[index] = { ...all[index], ...data, updatedAt: new Date() };
        localStorage.setItem(STORAGE_KEYS.TEAM, JSON.stringify(all));
        return all[index];
      }
    }
    return null;
  },

  async delete(id: string): Promise<boolean> {
    // Try API first
    const apiResult = await tryAPI('/api/team', 'DELETE', { id });
    if (apiResult !== null) return true;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      const filtered = all.filter((m) => m.id !== id);
      localStorage.setItem(STORAGE_KEYS.TEAM, JSON.stringify(filtered));
      return true;
    }
    return false;
  },
};

// ===== STATISTICS =====
export const statsStorage = {
  async getAll(): Promise<Statistic[]> {
    // Try API first
    const apiData = await tryAPI<Statistic[]>('/api/stats');
    if (apiData) return apiData;

    // Fallback to localStorage
    if (!isLocalStorage) return STATS_SEED_DATA;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.STATS);
      if (stored) return JSON.parse(stored);
      // Return seed data as default
      return STATS_SEED_DATA;
    } catch {
      return STATS_SEED_DATA;
    }
  },

  async create(data: Omit<Statistic, 'id' | 'createdAt' | 'updatedAt'>): Promise<Statistic> {
    const id = `stat_${Date.now()}`;
    const stat: Statistic = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Try API first
    const apiResult = await tryAPI<Statistic>('/api/stats', 'POST', stat);
    if (apiResult) return apiResult;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      all.push(stat);
      localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(all));
    }
    return stat;
  },

  async update(id: string, data: Partial<Statistic>): Promise<Statistic | null> {
    // Try API first
    const apiResult = await tryAPI<Statistic>('/api/stats', 'PUT', { id, ...data });
    if (apiResult) return apiResult;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      const index = all.findIndex((s) => s.id === id);
      if (index !== -1) {
        all[index] = { ...all[index], ...data, updatedAt: new Date() };
        localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(all));
        return all[index];
      }
    }
    return null;
  },

  async delete(id: string): Promise<boolean> {
    // Try API first
    const apiResult = await tryAPI('/api/stats', 'DELETE', { id });
    if (apiResult !== null) return true;

    // Fallback to localStorage
    if (isLocalStorage) {
      const all = await this.getAll();
      const filtered = all.filter((s) => s.id !== id);
      localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(filtered));
      return true;
    }
    return false;
  },
};

// ===== SITE SETTINGS =====
const DEFAULT_SETTINGS: SiteSettings = {
  id: 'default',
  companyName: 'Arithmify',
  tagline: 'Digital Services & Solutions',
  description: 'We build beautiful digital experiences',
  email: 'hello@arithmify.io',
  phone: '+1 (555) 000-0000',
  address: 'San Francisco, CA',
  linkedin: '',
  github: '',
  twitter: '',
  instagram: '',
  facebook: '',
};

export const settingsStorage = {
  async get(): Promise<SiteSettings> {
    // Try API first
    const apiData = await tryAPI<SiteSettings>('/api/settings');
    if (apiData) return apiData;

    // Fallback to localStorage
    if (!isLocalStorage) return DEFAULT_SETTINGS;
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      return stored ? JSON.parse(stored) : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  },

  async update(data: Partial<SiteSettings>): Promise<SiteSettings> {
    const merged = { ...DEFAULT_SETTINGS, ...data, id: 'default' };

    // Try API first
    const apiResult = await tryAPI<SiteSettings>('/api/settings', 'PUT', merged);
    if (apiResult) return apiResult;

    // Fallback to localStorage
    if (isLocalStorage) {
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(merged));
    }
    return merged;
  },
};

// ===== SECTION VISIBILITY =====
export const sectionVisibilityStorage = {
  async get() {
    try {
      const apiData = await tryAPI('/api/section-visibility');
      if (apiData) return apiData;

      if (!isLocalStorage) return null;
      const stored = localStorage.getItem('arithmify_section_visibility');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  async update(data: any) {
    const apiResult = await tryAPI('/api/section-visibility', 'PUT', data);
    if (apiResult) return apiResult;

    if (isLocalStorage) {
      localStorage.setItem('arithmify_section_visibility', JSON.stringify(data));
    }
    return data;
  },
};
